/**
 * Golf Configuration
 *
 * Contains scoring profiles, course presets, and hole configurations
 * based on real golf statistics and the USGA handicap system.
 */

// Score types relative to par
export const SCORE_TYPES = {
    EAGLE_OR_BETTER: 'eagleOrBetter',
    BIRDIE: 'birdie',
    PAR: 'par',
    BOGEY: 'bogey',
    DOUBLE_BOGEY: 'doubleBogey',
    TRIPLE_OR_WORSE: 'tripleOrWorse'
};

// Base probabilities by handicap bracket (derived from research data)
// Each profile represents the probability of each score type on an average hole
export const HANDICAP_SCORING_PROFILES = {
    0: {  // Scratch golfer
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.005,
        [SCORE_TYPES.BIRDIE]: 0.080,
        [SCORE_TYPES.PAR]: 0.670,
        [SCORE_TYPES.BOGEY]: 0.220,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.020,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.005
    },
    5: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.003,
        [SCORE_TYPES.BIRDIE]: 0.060,
        [SCORE_TYPES.PAR]: 0.500,
        [SCORE_TYPES.BOGEY]: 0.350,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.070,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.017
    },
    10: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.002,
        [SCORE_TYPES.BIRDIE]: 0.040,
        [SCORE_TYPES.PAR]: 0.380,
        [SCORE_TYPES.BOGEY]: 0.430,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.120,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.028
    },
    15: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.001,
        [SCORE_TYPES.BIRDIE]: 0.020,
        [SCORE_TYPES.PAR]: 0.280,
        [SCORE_TYPES.BOGEY]: 0.420,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.220,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.059
    },
    20: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.001,
        [SCORE_TYPES.BIRDIE]: 0.010,
        [SCORE_TYPES.PAR]: 0.190,
        [SCORE_TYPES.BOGEY]: 0.390,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.300,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.109
    },
    25: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.000,
        [SCORE_TYPES.BIRDIE]: 0.005,
        [SCORE_TYPES.PAR]: 0.120,
        [SCORE_TYPES.BOGEY]: 0.350,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.350,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.175
    },
    30: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.000,
        [SCORE_TYPES.BIRDIE]: 0.002,
        [SCORE_TYPES.PAR]: 0.080,
        [SCORE_TYPES.BOGEY]: 0.300,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.380,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.238
    },
    36: {
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0.000,
        [SCORE_TYPES.BIRDIE]: 0.001,
        [SCORE_TYPES.PAR]: 0.050,
        [SCORE_TYPES.BOGEY]: 0.250,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0.400,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0.299
    }
};

// Hole-type adjustments (shift probabilities based on par)
// These are additive adjustments to the base probabilities
export const HOLE_TYPE_ADJUSTMENTS = {
    3: {  // Par 3s - slightly harder for most golfers
        [SCORE_TYPES.EAGLE_OR_BETTER]: -0.003,  // Eagles very rare on par 3s
        [SCORE_TYPES.BIRDIE]: -0.010,
        [SCORE_TYPES.PAR]: -0.020,
        [SCORE_TYPES.BOGEY]: +0.020,
        [SCORE_TYPES.DOUBLE_BOGEY]: +0.010,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: +0.003
    },
    4: {  // Par 4s - baseline, no adjustment
        [SCORE_TYPES.EAGLE_OR_BETTER]: 0,
        [SCORE_TYPES.BIRDIE]: 0,
        [SCORE_TYPES.PAR]: 0,
        [SCORE_TYPES.BOGEY]: 0,
        [SCORE_TYPES.DOUBLE_BOGEY]: 0,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: 0
    },
    5: {  // Par 5s - easier for birdies, more scoring variance
        [SCORE_TYPES.EAGLE_OR_BETTER]: +0.008,
        [SCORE_TYPES.BIRDIE]: +0.025,
        [SCORE_TYPES.PAR]: +0.010,
        [SCORE_TYPES.BOGEY]: -0.025,
        [SCORE_TYPES.DOUBLE_BOGEY]: -0.015,
        [SCORE_TYPES.TRIPLE_OR_WORSE]: -0.003
    }
};

// Course presets with realistic ratings
export const COURSE_PRESETS = {
    easy: {
        id: 'easy',
        name: 'Friendly Links',
        par: 72,
        courseRating: 69.5,
        slopeRating: 113,
        description: 'A forgiving course suitable for all skill levels'
    },
    medium: {
        id: 'medium',
        name: 'County Golf Club',
        par: 72,
        courseRating: 72.0,
        slopeRating: 125,
        description: 'A well-balanced course with moderate difficulty'
    },
    hard: {
        id: 'hard',
        name: 'Championship Course',
        par: 72,
        courseRating: 74.5,
        slopeRating: 142,
        description: 'A challenging championship layout'
    }
};

// Standard 18-hole layout (4 par 3s, 10 par 4s, 4 par 5s)
// Stroke index indicates difficulty relative to other holes (1 = hardest)
export const STANDARD_HOLE_LAYOUT = [
    { number: 1, par: 4, strokeIndex: 7 },
    { number: 2, par: 4, strokeIndex: 13 },
    { number: 3, par: 5, strokeIndex: 3 },
    { number: 4, par: 3, strokeIndex: 15 },
    { number: 5, par: 4, strokeIndex: 1 },
    { number: 6, par: 4, strokeIndex: 9 },
    { number: 7, par: 3, strokeIndex: 17 },
    { number: 8, par: 5, strokeIndex: 5 },
    { number: 9, par: 4, strokeIndex: 11 },
    { number: 10, par: 4, strokeIndex: 8 },
    { number: 11, par: 4, strokeIndex: 2 },
    { number: 12, par: 3, strokeIndex: 16 },
    { number: 13, par: 5, strokeIndex: 4 },
    { number: 14, par: 4, strokeIndex: 10 },
    { number: 15, par: 4, strokeIndex: 6 },
    { number: 16, par: 3, strokeIndex: 18 },
    { number: 17, par: 4, strokeIndex: 12 },
    { number: 18, par: 5, strokeIndex: 14 }
];

/**
 * Generate a course configuration with standard hole layout
 * @param {Object} preset - Course preset from COURSE_PRESETS
 * @returns {Object} Complete course configuration
 */
export function generateCourseConfig(preset) {
    return {
        ...preset,
        holes: STANDARD_HOLE_LAYOUT.map(hole => ({ ...hole }))
    };
}

/**
 * Get handicap labels for display
 * @param {number} handicap - Handicap index value
 * @returns {string} Human-readable label
 */
export function getHandicapLabel(handicap) {
    if (handicap === 0) return 'Scratch';
    if (handicap <= 5) return 'Low Single Figure';
    if (handicap <= 9) return 'Single Figure';
    if (handicap <= 14) return 'Low Mid';
    if (handicap <= 18) return 'Mid Handicapper';
    if (handicap <= 24) return 'High Handicapper';
    if (handicap <= 30) return 'High Handicapper';
    return 'Beginner';
}

/**
 * Get consistency label for display
 * @param {number} consistency - Consistency value (0.15 to 0.35)
 * @returns {string} Human-readable label
 */
export function getConsistencyLabel(consistency) {
    if (consistency <= 0.18) return 'Very Consistent';
    if (consistency <= 0.22) return 'Consistent';
    if (consistency <= 0.28) return 'Average';
    if (consistency <= 0.32) return 'Variable';
    return 'Erratic';
}

// Score result labels for display
export const SCORE_LABELS = {
    [SCORE_TYPES.EAGLE_OR_BETTER]: 'Eagle or Better',
    [SCORE_TYPES.BIRDIE]: 'Birdie',
    [SCORE_TYPES.PAR]: 'Par',
    [SCORE_TYPES.BOGEY]: 'Bogey',
    [SCORE_TYPES.DOUBLE_BOGEY]: 'Double Bogey',
    [SCORE_TYPES.TRIPLE_OR_WORSE]: 'Triple+'
};

// Short score labels for compact display
export const SCORE_LABELS_SHORT = {
    [SCORE_TYPES.EAGLE_OR_BETTER]: 'Eagle+',
    [SCORE_TYPES.BIRDIE]: 'Birdie',
    [SCORE_TYPES.PAR]: 'Par',
    [SCORE_TYPES.BOGEY]: 'Bogey',
    [SCORE_TYPES.DOUBLE_BOGEY]: 'Double',
    [SCORE_TYPES.TRIPLE_OR_WORSE]: 'Triple+'
};

// Colours for score types (UK spelling as per project convention)
export const SCORE_COLOURS = {
    [SCORE_TYPES.EAGLE_OR_BETTER]: '#FFD700',  // Gold
    [SCORE_TYPES.BIRDIE]: '#DC2626',           // Red
    [SCORE_TYPES.PAR]: '#22C55E',              // Green
    [SCORE_TYPES.BOGEY]: '#9CA3AF',            // Light grey
    [SCORE_TYPES.DOUBLE_BOGEY]: '#6B7280',     // Medium grey
    [SCORE_TYPES.TRIPLE_OR_WORSE]: '#374151'   // Dark grey
};
